import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
    Box,
    CircularProgress,
    Grid2,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Button
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { warehouseDocumentAction } from "../asyncActions/warhouseActions";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

function WarehouseDetailPage() {
    const { t } = useTranslation();
    const { id } = useParams();

    const {
        warehouseDocument,
        warehouseDocumentProducts,
        dataLoad
    } = useSelector((store) => store.documentProducts);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(warehouseDocumentAction(id));
    }, [dispatch, id]);

    const rows = useMemo(() => {
        return warehouseDocumentProducts.map((item) => {
            console.log(item)
            const expected = parseFloat(item.expected_qnt);
            const actual = parseFloat(item.actual_qty);
            const unitType = (item.warhouse_product?.product)
                ? item.warhouse_product?.product?.unit_type
                : item.warhouse_product?.vendor_product?.unit_type;
            const diffValue = actual - expected;
            const diffPercent =
                expected !== 0
                    ? (((diffValue) / expected) * 100).toFixed(2)
                    : "N/A";

            const productName = (item.warhouse_product?.product)
                ? item.warhouse_product?.product?.name
                : item.warhouse_product?.vendor_product?.name;

            return {
                id: item.id,
                productName,
                expected_qnt: item.expected_qnt,
                actual_qty: item.actual_qty,
                diffPercent,
                unitType,
                diffValue: diffValue.toFixed(2)
            };
        });
    }, [warehouseDocumentProducts]);

    const getColor = (value) => {
        const num = parseFloat(value);
        if (isNaN(num)) return "inherit";
        if (num > 0) return "green";
        if (num < 0) return "red";
        return "inherit";
    };

    const renderTable = () => (
        <TableContainer component={Paper}>
            <Table aria-label="warehouse products table">
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Product Name</TableCell>
                        <TableCell>Unit Type</TableCell>
                        <TableCell align="right">Expected Qty</TableCell>
                        <TableCell align="right">Actual Qty</TableCell>
                        <TableCell align="right">Difference (Qty)</TableCell>
                        <TableCell align="right">Difference (%)</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.productName}</TableCell>
                            <TableCell>{row.unitType}</TableCell>
                            <TableCell align="right">{row.expected_qnt}</TableCell>
                            <TableCell align="right">{row.actual_qty}</TableCell>
                            <TableCell
                                align="right"
                                sx={{ color: getColor(row.diffValue) }}
                            >
                                {row.diffValue}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{ color: getColor(row.diffPercent) }}
                            >
                                {row.diffPercent}%
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const handleExport = () => {
        const cleanedRows = warehouseDocumentProducts.map((item) => {
            const expected = parseFloat(item.expected_qnt);
            const actual = parseFloat(item.actual_qty);
            const diffValue = actual - expected;
            const diffPercent =
                expected !== 0 ? (((diffValue) / expected) * 100).toFixed(2) : "N/A";

            const productName = item.warhouse_product?.product?.name ||
                item.warhouse_product?.vendor_product?.name || '';

            return {
                ID: item.id,
                "Product Name": productName,
                "Expected Qty": item.expected_qnt,
                "Actual Qty": item.actual_qty,
                "Difference (Qty)": diffValue.toFixed(2),
                "Difference (%)": diffPercent + '%',
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(cleanedRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Warehouse Data');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

        const warehouseName = 'warehouse_' + warehouseDocument.author?.username || 'warehouse';
        const date = new Date(warehouseDocument.date || new Date());
        const formattedDate = date.toISOString().split('T')[0];

        const fileName = `${warehouseName}_${formattedDate}.xlsx`;
        saveAs(blob, fileName);
    };


    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box
                sx={{
                    padding: "20px",
                    backgroundColor: "#f7f7f7",
                    borderRadius: "12px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    fontFamily: "Roboto, sans-serif",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px"
                }}
            >

                {dataLoad ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "20px" }}>
                            {t("page_text.document_detail")}
                        </Typography>
                        <Grid2 container spacing={5} justifyContent="center">
                            <Grid2 item xs={6}>
                                <Typography variant="body1">
                                    <strong>{t("page_text.doc_date")}:</strong> {warehouseDocument.date}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>{t("page_text.doc_author")}:</strong>{" "}
                                    {warehouseDocument.author?.username || "Unknown"}
                                </Typography>
                            </Grid2>
                            <Grid2 item xs={6}>
                                <Typography variant="body1">
                                    <strong>ID:</strong> {warehouseDocument.id}
                                </Typography>
                            </Grid2>
                        </Grid2>
                    </>
                )}
            </Box>
            <Box sx={{ marginTop: "20px", flexGrow: 1 }}>
                <Box sx={{ m: 1 }}>
                    <Button variant="contained" onClick={handleExport} sx={{ mt: 2 }}>
                        Export to Excel
                    </Button>
                </Box>
                <Box>
                    {!dataLoad && renderTable()}
                </Box>
            </Box>
        </Box>
    );
}

export default WarehouseDetailPage;